import React, { useEffect, useRef, useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";
import ToolJobsSection from "./shared/ToolJobsSection";
import useJobsPagination from "./shared/useJobsPagination";
import AmazonSourceSelector, {
  AMAZON_MODES,
  buildAmazonSourceFormData,
} from "./shared/AmazonSourceSelector";

const STATUS_DONE = "done";
const STATUS_FAILED = "failed";

const sourceLabel = (j) =>
  j.input_mode === "vendor"
    ? `Vendor: ${j.vendor}`
    : j.input_mode === "sku"
    ? `Parent SKU: ${j.parent_sku}`
    : `File: ${j.input_filename || "—"}`;

function AmazonPriceUpdateTool() {
  const [mode, setMode] = useState(AMAZON_MODES.VENDOR);
  const [file, setFile] = useState(null);
  const [vendor, setVendor] = useState("");
  const [parentSku, setParentSku] = useState("");
  const [dryRun, setDryRun] = useState(true); // guardrail: preview by default
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const {
    jobs,
    page,
    totalPages,
    totalFinished,
    setPage,
    refetch: refetchJobs,
  } = useJobsPagination({
    endpoint: "/amazon/price-update/jobs",
    cacheKey: "amazonPriceUpdate.jobs",
    pageSize: 10,
  });

  const toolInfo = {
    toolName: "Amazon Price Update",
    description:
      "Bring Amazon prices in line with Shopify (your MAP-compliant source of truth). For each Shopify variant, the target Amazon price is the Shopify selling price. Dry-run by default; every attempt is audit-logged.",
    steps: [
      "Choose the products: a Shopify vendor, an uploaded parent-SKU file, or a single parent SKU.",
      "Leave Dry Run ON to preview Amazon old → Shopify target price per SKU without touching Amazon.",
      "To write for real, turn Dry Run OFF — you'll be asked to confirm. Start with one vendor/SKU.",
      "Download the XLSX — each row shows Amazon Old Price, Shopify target, and the Action.",
    ],
    templates: [],
  };

  const guideTips = [
    "The target price is always the Shopify selling price — no price column to upload.",
    "Dry Run reads current Amazon prices and reports what WOULD change, mutating nothing.",
    "Every attempt (dry-run or live) is recorded in the amazon_write_audit log server-side.",
    "The price attribute Amazon expects is product-type-specific — validate a couple live writes in Seller Central before a large batch.",
  ];

  const previousStatusRef = useRef({});
  useEffect(() => {
    const previous = previousStatusRef.current;
    jobs.forEach((j) => {
      const prevStatus = previous[j.job_id];
      if (prevStatus && prevStatus !== j.status) {
        if (j.status === STATUS_DONE) addToast(`Price job ${j.job_id} complete.`, "success");
        else if (j.status === STATUS_FAILED)
          addToast(`Price job ${j.job_id} failed: ${j.error || "unknown error"}`, "error");
      }
    });
    previousStatusRef.current = Object.fromEntries(jobs.map((j) => [j.job_id, j.status]));
  }, [jobs, addToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    let formData;
    try {
      formData = buildAmazonSourceFormData({ mode, file, vendor, parentSku });
    } catch (e) {
      addToast(e.message, "error");
      return;
    }
    if (!dryRun) {
      const ok = window.confirm(
        "LIVE WRITE: this will set Amazon prices to the Shopify price for every matched " +
          "product. Continue?"
      );
      if (!ok) return;
    }
    formData.append("dry_run", dryRun ? "true" : "false");

    setUploading(true);
    setUploadProgress(0);
    try {
      const response = await flaskApi.post("/amazon/price-update", formData, {
        onUploadProgress: (e) =>
          setUploadProgress(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      const jobId = response.data?.job_id;
      if (!jobId) {
        addToast("Server did not return a job ID.", "error");
        return;
      }
      addToast(
        `Price job ${jobId} queued (${dryRun ? "dry run" : "LIVE"}). Tracking progress…`,
        "info"
      );
      if (page !== 1) await setPage(1);
      else await refetchJobs();
      setFile(null);
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to start price job.");
      addToast(message, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `/amazon/price-update/jobs/${job.job_id}/download`,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const cd =
        response.headers["content-disposition"] || response.headers["Content-Disposition"];
      link.setAttribute(
        "download",
        extractFilenameFromHeader(cd) || job.result_filename || "amazon_price_update.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast("Report downloaded.", "success");
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to download report.");
      addToast(message, "error");
    } finally {
      setDownloadingJobId(null);
    }
  };

  return (
    <>
      <ToolPageLayout
        splitLayout
        title="Amazon Price Update"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form">
          <div className="tool-form-main">
            <AmazonSourceSelector
              mode={mode}
              setMode={setMode}
              file={file}
              setFile={setFile}
              vendor={vendor}
              setVendor={setVendor}
              parentSku={parentSku}
              setParentSku={setParentSku}
              disabled={uploading}
            />

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Mode</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-checkbox-option">
                  <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                    disabled={uploading}
                  />
                  <span>Dry Run — preview Amazon old → Shopify target, no changes on Amazon</span>
                </label>
                <span className="tool-helper-text">
                  {dryRun
                    ? "Safe preview: reads current Amazon prices and reports what would change. Nothing on Amazon is modified."
                    : "⚠ LIVE: Amazon prices will be set to the Shopify price for every matched product. You'll be asked to confirm."}
                </span>
              </div>
            </section>

            <div className="tool-action-stack">
              <button type="submit" className="tool-btn-primary" disabled={uploading}>
                {uploading ? "Starting…" : dryRun ? "Run Dry Run" : "Run LIVE Update"}
              </button>
              <ToolProgressPanel
                loading={uploading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading…"
                processingMessage="Queueing job…"
              />
            </div>
          </div>
        </form>

        <ToolJobsSection
          title="Your Price Jobs"
          emptyText="No price jobs yet. Choose a source and run."
          jobs={jobs}
          page={page}
          totalPages={totalPages}
          totalFinished={totalFinished}
          onPageChange={setPage}
          renderJob={(j) => {
            const progress = j.progress || {};
            const total = Number(progress.total) || 0;
            const processed = Number(progress.processed) || 0;
            const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
            return (
              <div key={j.job_id} className="tool-job-status">
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Job ID</span>
                  <span className="tool-job-value">{j.job_id}</span>
                </div>
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Mode</span>
                  <span className="tool-job-value">{j.dry_run ? "DRY RUN" : "LIVE"}</span>
                </div>
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Source</span>
                  <span className="tool-job-value">{sourceLabel(j)}</span>
                </div>
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Status</span>
                  <span className={`tool-job-value tool-job-status-${j.status}`}>{j.status}</span>
                </div>
                {total > 0 ? (
                  <>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Progress</span>
                      <span className="tool-job-value">
                        {processed.toLocaleString()} / {total.toLocaleString()} ({pct}%)
                      </span>
                    </div>
                    <div className="progress-bar-container">
                      <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                  </>
                ) : null}
                <div className="tool-job-counts">
                  <span>Updated: {progress.updated || 0}</span>
                  <span>Planned: {progress.planned || 0}</span>
                  <span>Failed: {progress.failed || 0}</span>
                  <span>Skipped: {progress.skipped || 0}</span>
                </div>
                {j.status === STATUS_FAILED && j.error ? (
                  <div className="tool-job-error">Error: {j.error}</div>
                ) : null}
                {j.status === STATUS_DONE ? (
                  <button
                    type="button"
                    className="tool-btn-primary"
                    onClick={() => handleDownload(j)}
                    disabled={downloadingJobId === j.job_id}
                  >
                    {downloadingJobId === j.job_id ? "Downloading…" : "Download Report"}
                  </button>
                ) : null}
              </div>
            );
          }}
        />
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}

export default AmazonPriceUpdateTool;
